/**
 * The rates a carrier quoted, to pick one from. Shared because both resolution paths of the flow
 * end the same way - one re-quotes on edited shipment details, the other on a different return
 * method - and what the merchant chooses from is the same list either way.
 *
 * It is the generic SelectDropdown underneath, with each row drawn by ShippingRateOption.
 */
import { observer } from "mobx-react";
import { IOption, SelectDropdown } from "../../../../../../../components/inputs/select-dropdown/select-dropdown";
import { IPortalDynamicShippingOptionDto } from "../../../../rma-types";
import ShippingRateOption, { hasPrice } from "./shipping-rate-option/shipping-rate-option";
import "./shipping-rate-dropdown.scss";

interface IProps {
    rates: IPortalDynamicShippingOptionDto[];
    selectedRate: IPortalDynamicShippingOptionDto | null;
    onSelect: (rate: IPortalDynamicShippingOptionDto) => void;
    /** Set while the rates are being quoted: the field stays where it is and says it is working. */
    isLoading?: boolean;
}

const ShippingRateDropdown: React.FC<IProps> = observer(({ rates, selectedRate, onSelect, isLoading }) => {
    /**
     * Rates with no price are left out of the comparison rather than counted as free: an unsettled
     * fee is not a cheap one, and naming it the cheapest would send the merchant to the wrong rate.
     */
    const pricedCosts = rates.filter(hasPrice).map(({ cost }) => cost);
    const cheapestCost = pricedCosts.length ? Math.min(...pricedCosts) : null;

    const toOption = (rate: IPortalDynamicShippingOptionDto): IOption => ({
        value: rate.id,
        label: <ShippingRateOption rate={rate} isCheapest={hasPrice(rate) && rate.cost === cheapestCost} />,
    });

    /** The generic hands back the option; the owner is given the rate it stands for. */
    const selectRate = (option: IOption | null): void => {
        const rate = rates.find(({ id }) => id === option?.value);

        if (rate) {
            onSelect(rate);
        }
    };

    return (
        <div className="shipping-rate-dropdown">
            <SelectDropdown
                options={rates.map(toOption)}
                value={selectedRate ? toOption(selectedRate) : null}
                onChange={selectRate}
                isLoading={isLoading}
                isSearchable={false}
                placeholder="Select a shipping rate"
            />
        </div>
    );
});

export default ShippingRateDropdown;
